import { AsyncPipe, NgFor, NgIf, isPlatformServer } from '@angular/common';
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import {
  AssetDto,
  DisplayGuestDto,
  DisplayUserDto,
  GalleryAccessLevel,
  GalleryColorLabelDto,
  GalleryDto,
  GalleryFeedbackOption,
  SectionDto,
  TagDto,
} from '@picflow/types';
import { Subscription, firstValueFrom, lastValueFrom, map, of, switchMap, take, tap } from 'rxjs';
import { GalleryMemberService } from 'src/app/gallery-member.service';
import { GallerySettingsDialogComponent } from 'src/app/gallery/dialogs/gallery-settings-dialog/gallery-settings-dialog.component';
import { exifKeys } from 'src/app/lib/extract-exif';
import { sleep } from 'src/app/lib/sleep';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { DialogService } from 'src/app/services/dialog.service';
import { ExifService } from 'src/app/services/exif.service';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { GalleryService } from 'src/app/services/gallery.service';
import { PermissionService } from 'src/app/services/permission.service';
import { StateService } from 'src/app/services/state.service';
import { TenantService } from 'src/app/services/tenant.service';
import { UpdateService } from 'src/app/services/update.service';
import { DisplayGuestEmailDto, DisplayUserEmailDto, VisitorService } from 'src/app/services/visitor.service';
import { TogglerComponent } from 'src/app/shared-extra/components/toggler/toggler.component';
import { AvatarPipe } from 'src/app/shared-extra/pipes/avatar.pipe';
import { ResizeDirective } from 'src/app/shared/directives/resize.directive';
import { TagService } from 'src/app/tag.service';
import { ProfilePreviewComponent } from '../../../gallery/components/profile-preview/profile-preview.component';
import { TogglerComponent as TogglerComponent_1 } from '../../../shared-extra/components/toggler/toggler.component';
import { AvatarComponent } from '../../../shared/components/avatar/avatar.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SmoothCounterComponent } from '../../../shared/components/smooth-counter/smooth-counter.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TranslateLabelPipe } from '../../../shared/pipes/translate-label.pipe';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { Filter } from '../../interfaces/filter.interface';
import { filterAssets, singleFilter } from '../../lib/filter-assets';

@Component({
  selector: 'app-advanced-mode-sidebar',
  templateUrl: './advanced-mode-sidebar.component.html',
  styleUrls: ['./advanced-mode-sidebar.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    TogglerComponent_1,
    FormsModule,
    SvgComponent,
    NgFor,
    ButtonDirective,
    TranslatePipe,
    TranslateLabelPipe,
    AvatarPipe,
    AsyncPipe,
    ContextMenuComponent,
    RouterLink,
    ResizeDirective,
    ProfilePreviewComponent,
    SmoothCounterComponent,
    AvatarComponent,
  ],
})
export class AdvancedModeSidebarComponent implements OnChanges, OnDestroy {
  @Input() gallery: GalleryDto;
  @Input() sections: SectionDto[];
  @Input() assets: AssetDto[];
  @Input() _assets: AssetDto[];
  @Input() searchTerm: string;
  @Input() filters: Filter[];
  @Input() accessLevel: GalleryAccessLevel;
  @Input() allowedFeedbackOptions: GalleryFeedbackOption[] = [];
  @Input() allowedAndEnabledFeedbackOptions: GalleryFeedbackOption[] = [];
  @Input() colorLabels: GalleryColorLabelDto[] = [];
  @Input() isDesktop: boolean;
  @Output() onFilterChange = new EventEmitter();
  @Output() onSelectAll = new EventEmitter();
  @Input() people: {
    userOrGuest: DisplayUserEmailDto | DisplayGuestEmailDto;
    assetIds: string[];
    type: 'user' | 'guest';
  }[] = [];

  @ViewChild('peopleToggler') peopleToggler: TogglerComponent;
  @ViewChild('favouriteToggler') favouriteToggler: TogglerComponent;
  @ViewChild('flagToggler') flagToggler: TogglerComponent;
  @ViewChild('commentToggler') commentToggler: TogglerComponent;
  @ViewChild('annotationToggler') annotationToggler: TogglerComponent;
  @ViewChild('colorLabelToggler') colorLabelToggler: TogglerComponent;
  @ViewChild('exif1Toggler') exif1Toggler: TogglerComponent;
  @ViewChild('exif2Toggler') exif2Toggler: TogglerComponent;
  @ViewChild('tagsToggler') tagsToggler: TogglerComponent;

  subGalleries: GalleryDto[] = [];
  dynamicFilterValues: { [key: string]: string[] } = {};
  assetCounts: Map<string, number> = new Map();
  emailMap: { [key: string]: string } = {};
  isTeamMember: boolean;

  allFilters = {
    is_liked: { false: false, true: false },
    likes: { truthy: false, falsy: false },
    status: { no_status: false, approved: false, rejected: false },
    color_label: { null: false, red: false, orange: false, blue: false, purple: false, green: false },
    comments: { with: false, without: false },
    annotations: { with: false, without: false },
    people: {},
    iso: {},
    camera: {},
    lens: {},
    focalLength: {},
    aperture: {},
    shutterSpeed: {},
    tags: {},
  };

  updateSub: Subscription;

  tenantTags$ = this.tagService.tags$;

  galleryTags$ = of(undefined).pipe(
    switchMap(() => {
      if (this.accessLevel !== 'edit') return of([]);
      // if (!this.permissionService.hasRolePermission('tags:list')) {
      //   const tags: TagDto[] = [];
      //   this.assets.forEach(asset => {
      //     (asset?.['tags'] as TagDto[]).forEach(tag => {
      //       if (!tags.some(t => t.id === tag.id)) {
      //         tags.push(tag);
      //       }
      //     });
      //   });
      //   return of(tags);
      // }
      return this.tagService.tags$.pipe(
        map(tags => {
          return tags.filter(tag => this.assets.some(asset => (asset['tags'] as TagDto[])?.some(t => t.id === tag.id)));
        })
      );
    })
  );

  constructor(
    private galleryService: GalleryService,
    private dialog: DialogService,
    public featureFlags: FeatureFlagsService,
    private exifService: ExifService,
    private analytics: AnalyticsService,
    private tenantService: TenantService,
    private galleryMemberService: GalleryMemberService,
    private state: StateService,
    private updateService: UpdateService,
    private visitorService: VisitorService,
    private tagService: TagService,
    private permissionService: PermissionService,
    @Inject(PLATFORM_ID) private platformId: string
  ) {
    exifKeys.forEach(key => {
      this.dynamicFilterValues[key] = [];
    });

    this.updateSub = this.updateService.onAssetUpdate.subscribe(() => {
      this.calcAssetCounts();
    });
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (isPlatformServer(this.platformId)) return;
    if (changes.gallery) {
      const tenantId = this.gallery['tenant']?.['id'] || this.gallery['tenant'];
      this.isTeamMember = !!this.state.tenants?.find(t => t.id === tenantId);
      this.filters.forEach(filter => {
        const g = this.allFilters[filter.key];
        filter.values.forEach(value => (g[value] = true));
      });

      await lastValueFrom(this.createDynamicFilters());
    }

    this.getVisitors();
    await sleep(0);
    this.calcAssetCounts();
  }

  private async getVisitors() {
    const tenantId = this.gallery['tenant']?.['id'] || this.gallery['tenant'];
    const tenant = this.state.tenants?.find(t => t.id === tenantId);
    if (!tenant) return;
    const [visitors, galleryMembers, teamMembers] = await Promise.all([
      firstValueFrom(this.visitorService.getGalleryVisitors(this.gallery.id)),
      firstValueFrom(this.galleryMemberService.getAllGalleryMembers(true)),
      firstValueFrom(this.tenantService.getMembers()),
    ]);
    this.emailMap = {};
    visitors.forEach(r => {
      const id = <string>(r.user?.['id'] ?? r.guest?.['id']);
      const userOrGuest = <DisplayUserDto>r.user || <DisplayGuestDto>r.guest;
      this.emailMap[id] = userOrGuest['email'];
    });
    galleryMembers.forEach(gm => {
      this.emailMap[gm.user['id']] = gm.user['email'];
    });
    teamMembers.forEach(tm => {
      this.emailMap[tm.id] = tm.email;
    });
  }

  public createDynamicFilters() {
    this.dynamicFilterValues = {};
    const sets: { [key: string]: Set<string> } = {};
    exifKeys.forEach(key => (sets[key] = new Set()));

    this._assets.forEach(asset => {
      const exif = this.exifService.getExif(asset);
      exifKeys.forEach(key => sets[key].add(exif[key] || null));
    });

    exifKeys.forEach(key => {
      this.dynamicFilterValues[key] = Array.from(sets[key]);
      this.allFilters[key] = {};
      this.dynamicFilterValues[key].forEach(value => (this.allFilters[key][value] = false));

      this.dynamicFilterValues[key].sort((a, b) => {
        if (!a) return 1;
        if (!b) return -1;
        const intA = parseFloat(a);
        const intB = parseFloat(b);
        if (isNaN(intA) || isNaN(intB)) return a > b ? 1 : -1; // string compare
        return intA > intB ? 1 : -1;
      });
    });

    if (this.accessLevel !== 'edit') return of(undefined);

    return this.galleryTags$.pipe(
      take(1),
      tap(tags => {
        tags.forEach(tag => {
          this.allFilters.tags[tag.id] = false;
        });
      })
    );
  }

  handleFilter(toggleName?: string) {
    this.filters.length = 0;
    Object.entries(this.allFilters).forEach(([key, value]) => {
      const filter: Filter = { key, values: [] };
      for (const i in value) {
        const val: boolean | string = i;
        if (value[i]) filter.values.push(val);
      }
      if (filter.values.length) this.filters.push(filter);
    });
    this.onFilterChange.emit(undefined);
    if (toggleName) {
      this.analytics.track('am-toggle_' + toggleName);
    }
  }

  areChanges(key: string) {
    return !!this.filters.find(filter => filter.key === key);
  }

  getGalleryLink(gallery: GalleryDto) {
    const path = location.pathname
      .split(this.gallery.id)
      .join(gallery.id)
      .split(this.gallery.id.split('gal_')[1])
      .join(gallery.id)
      .split(this.gallery.path)
      .join(gallery.path);
    return path.split('/');
  }

  private getVisibleFilters() {
    const filters: typeof this.allFilters = {
      annotations: this.annotationToggler?.open ? this.allFilters.annotations : undefined,
      aperture: this.exif2Toggler?.open ? this.allFilters.aperture : undefined,
      camera: this.exif1Toggler?.open ? this.allFilters.camera : undefined,
      color_label: this.colorLabelToggler?.open ? this.allFilters.color_label : undefined,
      comments: this.commentToggler?.open ? this.allFilters.comments : undefined,
      focalLength: this.exif1Toggler?.open ? this.allFilters.focalLength : undefined,
      is_liked: this.favouriteToggler?.open ? this.allFilters.is_liked : undefined,
      iso: this.exif2Toggler?.open ? this.allFilters.iso : undefined,
      lens: this.exif1Toggler?.open ? this.allFilters.lens : undefined,
      likes: this.favouriteToggler?.open ? this.allFilters.likes : undefined,
      people: this.peopleToggler?.open ? this.allFilters.people : undefined,
      shutterSpeed: this.exif2Toggler?.open ? this.allFilters.shutterSpeed : undefined,
      status: this.flagToggler?.open ? this.allFilters.status : undefined,
      tags: this.tagsToggler?.open ? this.allFilters.tags : undefined,
    };

    return filters;
  }

  calcAssetCounts() {
    if (this.people) {
      this.people.forEach(p => {
        if (this.allFilters.people[p.userOrGuest['id']]) return;
        this.allFilters.people[p.userOrGuest['id']] = false; // add new people
      });
    }

    if (!this.filters) return;
    const allFilters = this.getVisibleFilters();
    for (const filterKey in allFilters) {
      if (allFilters[filterKey] === undefined) continue;
      const filters = this.filters.filter(filter => filter.key !== filterKey);
      const assets = filterAssets(this._assets || [], filters, this.searchTerm, false, this.people);
      const values = allFilters[filterKey];
      for (const value in values) {
        const filter: Filter = { key: filterKey, values: [value] };
        this.assetCounts.set(
          `${filterKey}_${value}`,
          assets.filter(img => !singleFilter(filter, img, this.people)).length
        );
      }
    }
    this.peopleToggler?.calcBodyHeight();
  }

  resetFilters() {
    this.filters.forEach(filter => {
      const g = this.allFilters[filter.key];
      for (const key in g) g[key] = false;
    });
    this.handleFilter();
  }

  selectAll() {
    this.onSelectAll.emit();
  }

  async openSettingsDialog(gallery = this.gallery) {
    this.dialog.open(GallerySettingsDialogComponent, { gallery, defaultMode: 'review' });
  }

  async moveGalleryOut(gallery: GalleryDto) {
    await this.galleryService.updateGallery(gallery.id, { section: null });
  }

  ngOnDestroy(): void {
    this.updateSub.unsubscribe();
  }
}
